import { Api } from '@/api/api';
import { HttpClient } from '@/api/generatedApi';
import { getApiBasePath, getUiBasePathPrefix } from '@/utils/basePath';
import { resolveRuntimeEnvironment } from '@/utils/runtimeEnvironment';
import { PATHS } from '@/utils/paths';
import { getAuthToken } from './auth/getAuthToken';
import { clearAuthSession } from './auth/clearAuthSession';
import { markSessionExpired } from './auth/sessionExpiredNotice';
import { savePostLoginRedirect } from './auth/postLoginRedirect';

/**
 * Full API base URL for all API calls (XHR, fetch, iframe src).
 * - ECS: VITE_API_BASE_URL is the API domain (e.g., https://dev3-api.physna.com) — required
 *   because UI and API are separate origins.
 * - EKS: VITE_API_BASE_URL is empty — nginx proxies API requests, so relative path works.
 * - Local dev: VITE_API_BASE_URL is http://localhost:3000 — Vite proxy forwards to API server.
 */
export const getBaseUrl = () => {
  const basePath = getApiBasePath() || '/v3';
  const apiBaseUrl = resolveRuntimeEnvironment(import.meta.env.VITE_API_BASE_URL, '');
  return apiBaseUrl ? `${apiBaseUrl}${basePath}` : basePath;
};

export const getApiDocumentationUrl = () => `${getBaseUrl()}/docs`;

// Enclave and gov tiers do not mount /v3/docs, so the link would 404 there. Unset means off,
// matching the api-server default, so a deployment that drops the variable hides the link on the
// same tiers that stop serving the route rather than advertising a 404.
// See architecture/decisions/2026-09-02-openapi-spec-and-docs-exposure.md.
export const isApiDocumentationEnabled = () =>
  resolveRuntimeEnvironment(import.meta.env.VITE_API_DOCUMENTATION_ENABLED, 'false') === 'true';

let isRedirectInProgress = false;

const SESSION_IDENTITY_PATH = '/users/me';

interface InterceptedError {
  response?: { status?: number };
  config?: { url?: string; meta?: { skipRedirect?: boolean } };
}

// The Cognito strategy answers an expired token with 403, not 401, because it raises
// ForbiddenError on a failed verification, and every deployed stack runs Cognito. (The
// OIDC and Cn strategies raise AuthenticationError there, so they arrive as 401.) A 403
// anywhere else is an ordinary permission denial, so only the endpoint that establishes
// who the caller is can tell the two apart: if that one refuses, no session is left to
// recover, whether the token died or the caller was never allowed one.
const isSessionUnrecoverable = ({ response, config }: InterceptedError): boolean => {
  if (response?.status === 401) {
    return true;
  }
  return response?.status === 403 && Boolean(config?.url?.endsWith(SESSION_IDENTITY_PATH));
};

// Nothing is discarded until a redirect is certain: the pages that handle their own
// auth errors keep the session they are still rendering against.
const endSessionAndRedirect = async (): Promise<void> => {
  const prefix = getUiBasePathPrefix();
  const isOnExceptionPage = redirectExceptions.some(path =>
    globalThis.location.pathname.startsWith(`${prefix}${path}`)
  );
  if (isOnExceptionPage) {
    isRedirectInProgress = false;
    return;
  }
  // Releasing the latch matters as much as not redirecting: signing in navigates by
  // pushState, which fires no popstate, so a latch left set here would survive into the
  // authenticated session and silently swallow the next dead session.
  if (globalThis.location.pathname.startsWith(`${prefix}${PATHS.SIGN_IN}`)) {
    isRedirectInProgress = false;
    return;
  }

  await clearAuthSession().catch((error: unknown) => {
    console.warn('Failed to clear the expired auth session', error);
  });

  // ProtectedRoute only records this when it already sees an unauthenticated user,
  // which is not this path, so without it every recovered session lands on /tenants.
  savePostLoginRedirect(globalThis.location);
  markSessionExpired();
  globalThis.location.assign(`${prefix}${PATHS.SIGN_IN}`);
};

const createHttpClient = (): HttpClient => {
  const client = new HttpClient({
    baseURL: getBaseUrl(),
    secure: true,
    withCredentials: true,
    securityWorker: async () => {
      const authToken = await getAuthToken();
      return {
        headers: {
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      };
    },
  });

  if (client.instance.interceptors?.response) {
    client.instance.interceptors.response.use(
      response => response,
      async (error: InterceptedError) => {
        const skipRedirect = error.config?.meta?.skipRedirect;
        if (error.response && !isRedirectInProgress && !skipRedirect) {
          if (isSessionUnrecoverable(error)) {
            isRedirectInProgress = true;
            await endSessionAndRedirect();
          } else {
            isRedirectInProgress = false;
          }
        }
        throw error;
      }
    );
  }

  return client;
};

const redirectExceptions = [PATHS.ACCESS_DENIED, PATHS.OAUTH2_REDIRECT, PATHS.OAUTH2_SIGNOUT];

const resetRedirectFlag = () => {
  isRedirectInProgress = false;
};

interface GlobalWithEventListeners {
  addEventListener: (type: string, listener: () => void) => void;
}

const hasEventListeners = (globalObject: unknown): globalObject is GlobalWithEventListeners => {
  return typeof globalObject === 'object' && globalObject !== null && 'addEventListener' in globalObject;
};

if (hasEventListeners(globalThis)) {
  globalThis.addEventListener('beforeunload', resetRedirectFlag);
  globalThis.addEventListener('popstate', resetRedirectFlag);
}

export const api = new Proxy({} as Api, {
  get: (_target, property) => {
    const client: HttpClient = createHttpClient();
    const freshApi: Api = new Api(client);
    return freshApi[property as keyof Api];
  },
});
